import trackUserAction from '@/infrastructure/sentry/useTrackEvent';
import useWidgetLanguage from '@/infrastructure/state/WidgetLanguage/useWidgetLanguage';
import {
  CountryFlagLanguageMap,
  LanguageOptions,
  Languages
} from '@/translations/languages';
import {
  Combobox,
  Group,
  Image,
  Input,
  InputBase,
  useCombobox
} from '@mantine/core';
import { FC, useMemo, useState } from 'react';

import styles from './index.module.css';

interface IProps {
  withName?: boolean;
  onChange?: () => void;
}

const LanguagePicker: FC<IProps> = ({ withName, onChange }) => {
  const { widgetLanguage, changeWidgetLanguage } = useWidgetLanguage();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption()
  });

  const languageOptions = useMemo(() => {
    return LanguageOptions.map(language => ({
      value: language,
      label: language.toUpperCase(),
      icon: CountryFlagLanguageMap[language]
    }));
  }, []);

  const [value, setValue] = useState<Languages | null>(Languages.en);

  const options = languageOptions.map(item => (
    <Combobox.Option value={item.value} key={item.value}>
      <Group gap="2px">
        <Image
          w={21}
          h={21}
          mr={3}
          radius={11}
          src={item.icon}
          className={styles.optionIcon}
        ></Image>
        {item.label}
      </Group>
    </Combobox.Option>
  ));

  const selectedLanguageFlag =
    widgetLanguage && CountryFlagLanguageMap[widgetLanguage.widgetLanguage];
  const selectedLanguageName = widgetLanguage && widgetLanguage.widgetLanguage;

  return (
    <Combobox
      variant="outline-invert"
      radius="sm"
      store={combobox}
      onOptionSubmit={val => {
        changeWidgetLanguage(val as Languages, true);
        combobox.closeDropdown();
        onChange?.();
        trackUserAction(`Clicked on change widget language to ${val}`);
      }}
    >
      <Combobox.Target>
        <InputBase
          id="language-picker"
          radius="md"
          classNames={{ input: styles.comboBoxInput }}
          className={styles.languagePicker}
          variant="outline-invert"
          component="button"
          type="button"
          pointer
          h="34px"
          mih="34px"
          style={{ height: '34px', minHeight: '34px' }}
          rightSection={<Combobox.Chevron />}
          rightSectionPointerEvents="none"
          onClick={() => combobox.toggleDropdown()}
        >
          {value ? (
            <div className={styles.valueWrapper}>
              {withName && selectedLanguageName}
              <Image
                w={20}
                h={20}
                radius={10}
                src={selectedLanguageFlag}
              ></Image>
            </div>
          ) : (
            <Input.Placeholder>Pick value</Input.Placeholder>
          )}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>{options}</Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default LanguagePicker;
